import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  GraduationCap, 
  MapPin, 
  TrendingUp, 
  ExternalLink, 
  Calendar,
  DollarSign,
  Award,
  Users,
  BookOpen,
  Building2,
  Phone,
  Mail,
  Globe,
  CheckCircle2,
  Star,
  Briefcase,
  Home,
  Library,
  Trophy
} from "lucide-react";

const CollegeDetail = () => {
  const { collegeId } = useParams();
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [college, setCollege] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCollegeDetails();
  }, [collegeId]);

  const fetchCollegeDetails = async () => {
    try {
      const response = await axios.get(`${API}/colleges/${collegeId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setCollege(response.data.college || response.data);
    } catch (error) {
      console.error("Error loading college details:", error);
      toast.error("Failed to load college details");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  if (!college) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <Card className="p-8 text-center">
            <GraduationCap className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h2 className="text-2xl font-bold mb-2">College Not Found</h2>
            <p className="text-gray-600 mb-4">The college you're looking for doesn't exist.</p>
            <Button onClick={() => navigate("/colleges")}>Back to Colleges</Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button variant="outline" onClick={() => navigate("/colleges")} className="mb-4">
          ← Back to Colleges
        </Button>

        {/* Hero Section with Image */}
        {college.image && (
          <div className="w-full h-64 md:h-96 overflow-hidden rounded-xl mb-6 shadow-lg">
            <img 
              src={college.image} 
              alt={college.name}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* College Header */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex-1">
                <div className="flex flex-wrap gap-2 mb-3">
                  {college.ranking?.nirf && (
                    <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300">
                      <TrendingUp className="w-4 h-4 mr-1" />
                      NIRF Rank #{college.ranking.nirf}
                    </Badge>
                  )}
                  {college.ranking?.qs_world && (
                    <Badge variant="outline">
                      QS World Rank #{college.ranking.qs_world}
                    </Badge>
                  )}
                  {college.is_featured && (
                    <Badge className="bg-green-100 text-green-700 border-green-300">
                      <Star className="w-4 h-4 mr-1" />
                      Featured College
                    </Badge>
                  )}
                  <Badge variant="outline">{college.type}</Badge>
                </div>
                <CardTitle className="text-3xl md:text-4xl mb-3">{college.name}</CardTitle>
                <div className="flex items-start gap-2 text-gray-600 mb-2">
                  <MapPin className="w-5 h-5 mt-1 flex-shrink-0" />
                  <span className="text-lg">{college.location}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-5 h-5" />
                  <span>Established: {college.established}</span>
                </div>
              </div>
              
              <div className="flex flex-col gap-2">
                <Button asChild className="w-full md:w-auto">
                  <a href={college.website} target="_blank" rel="noopener noreferrer">
                    <Globe className="w-4 h-4 mr-2" />
                    Visit Website
                  </a>
                </Button>
                {college.contact?.email && (
                  <Button variant="outline" asChild>
                    <a href={`mailto:${college.contact.email}`}>
                      <Mail className="w-4 h-4 mr-2" />
                      Contact
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Description */}
        {college.description && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                About
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed">{college.description}</p>
            </CardContent>
          </Card>
        )}

        {/* Tabs for Detailed Information */}
        <Tabs defaultValue="courses" className="mb-6">
          <TabsList className="flex flex-wrap h-auto gap-1 p-1 bg-muted/50 rounded-lg">
            <TabsTrigger value="courses" className="flex-1 min-w-[80px] text-xs sm:text-sm py-2 px-2 sm:px-3">
              <GraduationCap className="w-3 h-3 sm:w-4 sm:h-4 mr-1 hidden sm:inline" />
              Courses
            </TabsTrigger>
            <TabsTrigger value="admission" className="flex-1 min-w-[80px] text-xs sm:text-sm py-2 px-2 sm:px-3">
              <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 hidden sm:inline" />
              Admission
            </TabsTrigger>
            <TabsTrigger value="placements" className="flex-1 min-w-[80px] text-xs sm:text-sm py-2 px-2 sm:px-3">
              <Briefcase className="w-3 h-3 sm:w-4 sm:h-4 mr-1 hidden sm:inline" />
              Placements
            </TabsTrigger>
            <TabsTrigger value="infrastructure" className="flex-1 min-w-[80px] text-xs sm:text-sm py-2 px-2 sm:px-3">
              <Building2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 hidden sm:inline" />
              Infrastructure
            </TabsTrigger>
            <TabsTrigger value="scholarships" className="flex-1 min-w-[80px] text-xs sm:text-sm py-2 px-2 sm:px-3">
              <Award className="w-3 h-3 sm:w-4 sm:h-4 mr-1 hidden sm:inline" />
              Scholarships
            </TabsTrigger>
          </TabsList>

          {/* Courses Tab */}
          <TabsContent value="courses">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="w-5 h-5" />
                  Courses Offered
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {college.courses_offered && college.courses_offered.map((course, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm font-medium">{course}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Admission Tab */}
          <TabsContent value="admission">
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Admission Criteria
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {college.admission_criteria?.undergraduate && college.admission_criteria.undergraduate !== "Not Applicable" && (
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="font-semibold text-blue-900 mb-1">Undergraduate:</p>
                      <p className="text-blue-800">{college.admission_criteria.undergraduate}</p>
                    </div>
                  )}
                  {college.admission_criteria?.postgraduate && college.admission_criteria.postgraduate !== "Not Applicable" && (
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="font-semibold text-green-900 mb-1">Postgraduate:</p>
                      <p className="text-green-800">{college.admission_criteria.postgraduate}</p>
                    </div>
                  )}
                  {college.cutoff_marks && (
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <p className="font-semibold text-purple-900 mb-1">Cutoff Marks:</p>
                      <p className="text-purple-800">{college.cutoff_marks}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    Fee Structure
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {college.fees?.undergraduate && college.fees.undergraduate !== "N/A" && (
                    <div className="p-4 bg-amber-50 rounded-lg">
                      <p className="font-semibold text-amber-900 mb-1">Undergraduate (Annual):</p>
                      <p className="text-amber-800 text-lg">{college.fees.undergraduate}</p>
                    </div>
                  )}
                  {college.fees?.postgraduate && college.fees.postgraduate !== "N/A" && (
                    <div className="p-4 bg-amber-50 rounded-lg">
                      <p className="font-semibold text-amber-900 mb-1">Postgraduate (Annual):</p>
                      <p className="text-amber-800 text-lg">{college.fees.postgraduate}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Placements Tab */}
          <TabsContent value="placements">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5" />
                  Placement Statistics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {college.placements && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 bg-green-50 rounded-lg text-center">
                        <p className="text-sm text-green-700 mb-1">Average Package</p>
                        <p className="text-2xl font-bold text-green-900">{college.placements.average_package}</p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <p className="text-sm text-blue-700 mb-1">Highest Package</p>
                        <p className="text-2xl font-bold text-blue-900">{college.placements.highest_package}</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg text-center">
                        <p className="text-sm text-purple-700 mb-1">Placement Rate</p>
                        <p className="text-2xl font-bold text-purple-900">{college.placements.placement_rate}</p>
                      </div>
                    </div>

                    {college.placements.top_recruiters && college.placements.top_recruiters.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-3">Top Recruiters:</h4>
                        <div className="flex flex-wrap gap-2">
                          {college.placements.top_recruiters.map((recruiter, idx) => (
                            <Badge key={idx} variant="outline" className="text-sm px-3 py-1">
                              {recruiter}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Infrastructure Tab */}
          <TabsContent value="infrastructure">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Infrastructure & Facilities
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {college.infrastructure && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {college.infrastructure.library && (
                      <div className="p-4 bg-indigo-50 rounded-lg">
                        <div className="flex items-start gap-3">
                          <Library className="w-5 h-5 text-indigo-600 mt-1" />
                          <div>
                            <p className="font-semibold text-indigo-900 mb-1">Library</p>
                            <p className="text-sm text-indigo-800">{college.infrastructure.library}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {college.infrastructure.hostels && (
                      <div className="p-4 bg-pink-50 rounded-lg">
                        <div className="flex items-start gap-3">
                          <Home className="w-5 h-5 text-pink-600 mt-1" />
                          <div>
                            <p className="font-semibold text-pink-900 mb-1">Hostels</p>
                            <p className="text-sm text-pink-800">{college.infrastructure.hostels}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {college.infrastructure.labs && (
                      <div className="p-4 bg-cyan-50 rounded-lg">
                        <div className="flex items-start gap-3">
                          <Building2 className="w-5 h-5 text-cyan-600 mt-1" />
                          <div>
                            <p className="font-semibold text-cyan-900 mb-1">Labs & Research</p>
                            <p className="text-sm text-cyan-800">{college.infrastructure.labs}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {college.infrastructure.sports && (
                      <div className="p-4 bg-orange-50 rounded-lg">
                        <div className="flex items-start gap-3">
                          <Trophy className="w-5 h-5 text-orange-600 mt-1" />
                          <div>
                            <p className="font-semibold text-orange-900 mb-1">Sports</p>
                            <p className="text-sm text-orange-800">{college.infrastructure.sports}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {college.infrastructure?.other && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="font-semibold text-gray-900 mb-2">Other Facilities:</p>
                    <p className="text-sm text-gray-800">{college.infrastructure.other}</p>
                  </div>
                )}

                {college.accreditation && college.accreditation.length > 0 && (
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Award className="w-5 h-5 text-green-600 mt-1" />
                      <div>
                        <p className="font-semibold text-green-900 mb-2">Accreditation:</p>
                        <div className="flex flex-wrap gap-2">
                          {college.accreditation.map((acc, idx) => (
                            <Badge key={idx} className="bg-green-100 text-green-700 border-green-300">
                              {acc}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scholarships Tab */}
          <TabsContent value="scholarships">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  Scholarships Available
                </CardTitle>
              </CardHeader>
              <CardContent>
                {college.scholarships && college.scholarships.length > 0 ? (
                  <div className="space-y-2">
                    {college.scholarships.map((scholarship, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg">
                        <CheckCircle2 className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{scholarship}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600">No scholarship information available.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Contact Information */}
        {college.contact && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="w-5 h-5" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gray-600" />
                <a href={`tel:${college.contact.phone}`} className="text-blue-600 hover:underline">
                  {college.contact.phone}
                </a>
              </div>
              {college.contact.email && (
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gray-600" />
                  <a href={`mailto:${college.contact.email}`} className="text-blue-600 hover:underline">
                    {college.contact.email}
                  </a>
                </div>
              )}
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-gray-600" />
                <a href={college.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  {college.website}
                </a>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default CollegeDetail;
